<script setup lang="ts">
import { computed, h, ref, type VNodeChild } from 'vue';
import { NFlex, NIcon, NTag, type SelectOption } from 'naive-ui';
import GaiaRemoteSelect, { type GaiaRemotePage } from '../../base/selects/GaiaRemoteSelect.vue';
import GaiaSegmented from '../../base/GaiaSegmented.vue';
import GaiaText from '../../base/GaiaText.vue';
import { IconPeople } from '../../icons';
import { PermissionsService } from '@/services/permissionsService';

/**
 * Picks who to share with — people and roles in one field. A switch at the top
 * of the menu says which one the search looks through (never both at once),
 * while the chips keep every pick from either side. Roles carry the group
 * glyph so one can't be mistaken for a person of that name, and the picks
 * come back split: `v-model:users` and `v-model:roles`, uids.
 *
 * The built-in "Organisation Member" role stays in: shared with it, a record
 * reaches everyone in the organisation.
 */
const users = defineModel<string[]>('users', { default: () => [] });
const roles = defineModel<string[]>('roles', { default: () => [] });

/** Per request — the search endpoints cap a page at 50. */
const PAGE_SIZE = 50;

/** Which side the search looks through. */
const scope = ref<'user' | 'role'>('user');
const remoteRef = ref<{ clearSearch: () => void } | null>(null);

/** A term typed for one side means nothing on the other: switching clears it. */
function switchScope(next: string): void {
  scope.value = next === 'role' ? 'role' : 'user';
  remoteRef.value?.clearSearch();
}
const SCOPE_OPTIONS = [
  { label: 'People', value: 'user' },
  { label: 'Roles', value: 'role' },
];

/** The field holds both kinds in one list, told apart by a prefix. */
type RecipientKind = 'user' | 'role';
interface RecipientOption extends SelectOption {
  value: string;
  label: string;
  kind: RecipientKind;
  /** Second line in the menu: a person's email, or "Role". */
  meta: string;
}

const value = computed<string[]>({
  get: () => [...users.value.map(uid => `user:${uid}`), ...roles.value.map(uid => `role:${uid}`)],
  set: next => {
    users.value = next.filter(key => key.startsWith('user:')).map(key => key.slice(5));
    roles.value = next.filter(key => key.startsWith('role:')).map(key => key.slice(5));
  },
});

async function fetchPeople(query: string, page: number): Promise<GaiaRemotePage> {
  const { results, result_count } = await PermissionsService.searchUsers({
    text: query,
    page,
    pageSize: PAGE_SIZE,
    sortKey: 'email',
    sortDirection: 'asc',
  });
  const options: RecipientOption[] = results.map(user => ({
    value: `user:${user.uid}`,

    label: user.full_name || user.email,
    kind: 'user',
    meta: user.email,
  }));
  return { options, hasMore: page * PAGE_SIZE < result_count };
}

async function fetchRoles(query: string, page: number): Promise<GaiaRemotePage> {
  const { results, result_count } = await PermissionsService.searchRoles({
    text: query,
    page,
    pageSize: PAGE_SIZE,
    sortKey: 'name',
    sortDirection: 'asc',
  });
  const options: RecipientOption[] = results.map(role => ({
    value: `role:${role.uid}`,
    label: role.name,
    kind: 'role',
    meta: 'Role',
  }));
  return { options, hasMore: page * PAGE_SIZE < result_count };
}

/**
 * Every option seen, by value. The menu lists only the current scope, so a
 * chip from the other side is labelled from here (`fallback-option`) rather
 * than by putting it back into the menu, where it would mix the two lists.
 */
const seen = new Map<string, RecipientOption>();

function remember(page: GaiaRemotePage): GaiaRemotePage {
  for (const option of page.options as RecipientOption[]) seen.set(option.value, option);
  return page;
}

function fallbackOption(value: string | number): SelectOption {
  return seen.get(String(value)) ?? { label: String(value), value };
}

/** Switching scope swaps the fetch, which re-runs the search against it. */
const fetchRecipients = computed(() => {
  const fetchScope = scope.value === 'user' ? fetchPeople : fetchRoles;
  return async (query: string, page: number): Promise<GaiaRemotePage> =>
    remember(await fetchScope(query, page));
});
const placeholder = computed(() => (scope.value === 'user' ? 'Search people' : 'Search roles'));

function isRole(option: SelectOption): boolean {
  return (option as RecipientOption).kind === 'role';
}

/** Menu row: the name, and under it the email (or "Role"). */
function renderLabel(option: SelectOption): VNodeChild {
  const recipient = option as RecipientOption;
  return h(NFlex, { vertical: true, size: 0, style: { padding: '3px 0' } }, () => [
    h(GaiaText, { size: 'dense', tone: 'ink' }, () => recipient.label),
    h(GaiaText, { size: 'micro', tone: 'subtle' }, () => recipient.meta),
  ]);
}

/** Chip in the field: a role carries the group glyph, a person just a name. */
function renderTag({
  option,
  handleClose,
}: {
  option: SelectOption;
  handleClose: () => void;
}): VNodeChild {
  return h(
    NTag,
    { size: 'small', closable: true, onClose: handleClose },
    {
      default: (): string => option.label as string,
      icon: isRole(option) ? (): VNodeChild => h(NIcon, { component: IconPeople }) : undefined,
    }
  );
}
</script>

<template>
  <gaia-remote-select
    ref="remoteRef"
    v-model:value="value"
    multiple
    :fetch="fetchRecipients"
    :placeholder="placeholder"
    :render-label="renderLabel"
    :render-tag="renderTag"
    :keep-selected="false"
    :fallback-option="fallbackOption"
  >
    <template #header>
      <gaia-segmented :value="scope" :options="SCOPE_OPTIONS" block @update:value="switchScope" />
    </template>
  </gaia-remote-select>
</template>
